import videojs from 'video.js';

import PollIcon from '../../assets/images/poll-white-24dp.svg';
import { isPresentationId } from '../../util';

const _enabled = new WeakMap();
const _url = new WeakMap();
const _popup = new WeakMap();
let localize;

/**
 * The Polls module.
 * @member {PollsPlugin} polls
 * @memberof MediasitePlayer#
 */

/**
 * Configures the Polls button in the control bar menu.<br><br>
 * Note: the Polls button will only be displayed in the control bar menu if {@link PollsPlugin#enabled} is set to <code>true</code>, and {@link PollsPlugin#url} has been set to a non-empty string.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class PollsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);
        _enabled.set(this, false);

        player.ready(() => {
            player.controlbarMenu().addMenuItem(localize('Show Polls'), { id: this.name, icon: PollIcon, callback: () => this.#handleClick() });
        });
    }

    #handleClick() {
        this.open();
    }

    /**
     * The path to the polling webpage.
     * @type {string}
     * @example
     * player.polls.url = 'https://mediasite.com';
     */
    get url() {
        return _url.get(this);
    }

    set url (url) {
        _url.set(this, url);
        this.#updateVisibility();
    }

    /**
     * A boolean indicating whether or not the Polls feature is enabled.
     * @type {boolean}
     * @example
     * player.polls.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.#updateVisibility();
    }

    #updateVisibility() {
        if (_enabled.get(this) && _url.get(this)) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
        }
    }

    /**
     * Opens the configured polling URL in a new popup window.
     * @example
     * player.polls.open();
     */
    open() {
        if (!this.enabled) {
            return;
        }
        // Opens the popout window
        // Todo: handle original playFrom and duration query string params, if present

        // Note: we can only do this if we know the presentation ID.
        if (isPresentationId(this.player.loader().presentationId)) {
            const popup = window.open(_url.get(this), 'polls',
                `width=800,height=600,scrollbars=yes,resizable=yes`
            );
            popup.focus();
            _popup.set(this, popup);
        }
    }

    /**
     * Closes the popup window if it is currently open.
     * @example
     * player.polls.close();
     */
    close() {
        _popup.get(this)?.close?.();
    }

    /**
     * A boolean indicating whether or not the popup window is currently open.
     * @type {boolean}
     * @example
     * const isPollModalOpen = player.polls.isOpen();
     */
    get isOpen() {
        const popup = _popup.get(this);
        return !(!popup || (popup?.closed));
    }

    dispose() {
        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }
        
        super.dispose();
    }
}

export default PollsPlugin;
